import { Routes, Route } from 'react-router-dom';
import { useMemo } from 'react';
import { MainContextProvider } from '../context/storeContext';
import { SetPageTitle } from '../components/global/PageTitles/SetPageTitle';
import { Notification } from '../components/global/Notification/Notification';
import Preloader from '../components/global/preloader/Preloader';
import RequireAuth from '../hooks/RequireAuth';
import { Layout } from '../components/layout/Layout';
import { LoginPage } from '../pages/auth/login';
import ProfilePage from '../pages/profile/Profile';
import MenuMobile from '../pages/menu/Menu';
import Users from '../pages/users/Users';
import Meditations from '../pages/meditations/Meditations';
import Banners from '../pages/banners/Banners';
import MessageDay from '../pages/messageDay/MessageDay';
import Videos from '../pages/videos/Videos';
import Courses from '../pages/courses/Courses';
import Events from '../pages/events/Events';
import Promo from '../pages/promo/Promo';
import HiddenSectionEmails from '../pages/hiddenSectionEmails/HiddenSectionEmails';
import Faq from '../pages/faq/Faq';
import Contacts from '../pages/contacts/Contacts';
import PushNotificationsPage from '../pages/pushNotifications/pushNotifications';
import Settings from '../pages/settings/Settings';
import AccessGrants from '../pages/accessGrants/AccessGrants';
import Review from '../pages/review/Review';
import ReviewSubmission from '../pages/review/ReviewSubmission';
import { AppSettings } from '../appSettings';
import ApiClientProps from '../api/ApiClientProps';
import { useCookies } from 'react-cookie';

export const AllRoutes = (): JSX.Element => {
  const [cookies, setCookie, removeCookie] = useCookies();

  const apiProps = useMemo((): ApiClientProps => {
    return {
      baseUrl: AppSettings.api.url,
      debug: false,
      auth: {
        access: {
          name: AppSettings.auth.access.name,
          durationDays: AppSettings.auth.access.durationDays,
        },
        refresh: {
          name: AppSettings.auth.refresh.name,
          durationDays: AppSettings.auth.refresh.durationDays,
        },
      },
      saveToken: (key: string, value: string) => {
        setCookie(key, value, { path: '/' });
      },
      deleteToken: (key: string) => {
        removeCookie(key, { path: '/' });
      },
      getToken: (key: string) => {
        const token = cookies[key];
        return token;
      },
    };
  }, [cookies, setCookie, removeCookie]);

  return (
    <MainContextProvider api={apiProps}>
      <SetPageTitle />
      <Notification />
      <Preloader />
      <Routes>
        <Route
          path="/"
          element={
            <RequireAuth>
              <LoginPage />
            </RequireAuth>
          }
        />
        <Route path="/" element={<Layout />}>
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <ProfilePage />
              </RequireAuth>
            }
          />
          <Route
            path="/menu"
            element={
              <RequireAuth>
                <MenuMobile />
              </RequireAuth>
            }
          />
          <Route
            path="/users"
            element={
              <RequireAuth>
                <Users />
              </RequireAuth>
            }
          />
          <Route
            path="/meditations"
            element={
              <RequireAuth>
                <Meditations />
              </RequireAuth>
            }
          />
          <Route
            path="/banners"
            element={
              <RequireAuth>
                <Banners />
              </RequireAuth>
            }
          />
          <Route
            path="/message-day"
            element={
              <RequireAuth>
                <MessageDay />
              </RequireAuth>
            }
          />
          <Route
            path="/videos"
            element={
              <RequireAuth>
                <Videos />
              </RequireAuth>
            }
          />
          <Route
            path="/courses"
            element={
              <RequireAuth>
                <Courses />
              </RequireAuth>
            }
          />
          <Route
            path="/courses/:courseId"
            element={
              <RequireAuth>
                <Courses />
              </RequireAuth>
            }
          />
          <Route
            path="/courses/:courseId/:moduleId"
            element={
              <RequireAuth>
                <Courses />
              </RequireAuth>
            }
          />
          <Route
            path="/courses/:courseId/:moduleId/:lessonId"
            element={
              <RequireAuth>
                <Courses />
              </RequireAuth>
            }
          />
          <Route
            path="/courses/:courseId/:moduleId/:lessonId/assignment"
            element={
              <RequireAuth>
                <Courses />
              </RequireAuth>
            }
          />
          <Route
            path="/events"
            element={
              <RequireAuth>
                <Events />
              </RequireAuth>
            }
          />
          <Route
            path="/promo"
            element={
              <RequireAuth>
                <Promo />
              </RequireAuth>
            }
          />
          <Route
            path="/hidden-section-emails"
            element={
              <RequireAuth>
                <HiddenSectionEmails />
              </RequireAuth>
            }
          />
          <Route
            path="/faq"
            element={
              <RequireAuth>
                <Faq />
              </RequireAuth>
            }
          />
          <Route
            path="/contacts"
            element={
              <RequireAuth>
                <Contacts />
              </RequireAuth>
            }
          />
          <Route
            path="/push-notifications"
            element={
              <RequireAuth>
                <PushNotificationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <Settings />
              </RequireAuth>
            }
          />
          <Route
            path="/access-grants"
            element={
              <RequireAuth>
                <AccessGrants />
              </RequireAuth>
            }
          />
          <Route
            path="/review"
            element={
              <RequireAuth>
                <Review />
              </RequireAuth>
            }
          />
          <Route
            path="/review/:submissionId"
            element={
              <RequireAuth>
                <ReviewSubmission />
              </RequireAuth>
            }
          />
        </Route>
      </Routes>
    </MainContextProvider>
  );
};
